import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Edit, Trash2, Plus, Search, User, RefreshCw } from 'lucide-react';
import { fetchUsers, deleteUser, renewUserSubscription } from '../api/usersApi';

const AdminUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            setLoading(true);
            const data = await fetchUsers();
            setUsers(data);
        } catch (error) {
            console.error('Error loading users:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Estás seguro de que quieres eliminar este usuario?')) {
            try {
                await deleteUser(id);
                await loadUsers();
            } catch (error) {
                console.error('Error deleting user:', error);
                alert('Error al eliminar el usuario');
            }
        }
    };

    const handleRenew = async (id) => {
        if (window.confirm('¿Renovar subscripción de este usuario al siguiente ciclo?')) {
            try {
                await renewUserSubscription(id);
                await loadUsers();
            } catch (error) {
                console.error('Error renewing subscription:', error);
                alert('Error al renovar la subscripción');
            }
        }
    };

    const filteredUsers = users.filter(user =>
        user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phoneNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.essenNumber?.toString().includes(searchTerm)
    );

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
            </div>
        );
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Usuarios</h1>
                    <p className="text-gray-500 mt-1">Gestiona los usuarios del sistema</p>
                </div>
                <Link
                    to="/admin/platformUsers/new"
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                >
                    <Plus className="w-5 h-5" />
                    Nuevo Usuario
                </Link>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                    <div className="relative max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Buscar por nombre, apellido, teléfono o número Essen..."
                            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 text-gray-600 font-medium text-sm uppercase tracking-wider">
                            <tr>
                                <th className="px-6 py-4">Usuario</th>
                                <th className="px-6 py-4">Teléfono</th>
                                <th className="px-6 py-4">Nro Essen</th>
                                <th className="px-6 py-4">Interacciones</th>
                                <th className="px-6 py-4">Licencia</th>
                                <th className="px-6 py-4">Vence</th>
                                <th className="px-6 py-4 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {filteredUsers.map((user) => (
                                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                                                <User className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-900">{user.firstName} {user.lastName}</p>
                                                <p className="text-sm text-gray-500">{user.endPoint}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        {user.phoneNumber ? (
                                            <a 
                                                href={`https://wa.me/${user.phoneNumber.replace(/[^0-9+]/g, '')}`}
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="text-green-600 hover:text-green-700 hover:underline font-medium"
                                                title="Abrir WhatsApp"
                                            >
                                                {user.phoneNumber}
                                            </a>
                                        ) : (
                                            <span className="text-gray-400">-</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-gray-600">{user.essenNumber}</td>
                                    <td className="px-6 py-4 text-gray-600 font-bold">{user.interactions || 0}</td>
                                    <td className="px-6 py-4 text-gray-600">{user.license}</td>
                                    <td className="px-6 py-4">
                                        {(() => {
                                            if (user.license === 'Premium') {
                                                return <span className="text-purple-700 bg-purple-50 px-2 py-1 rounded border border-purple-100 font-bold">Sin vencimiento</span>;
                                            }
                                            if (!user.subscriptionExpirationDate) return <span className="text-gray-600 font-medium">-</span>;
                                            
                                            const expDate = new Date(user.subscriptionExpirationDate + 'T00:00:00');
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            
                                            const isExpired = expDate < today;
                                            
                                            return (
                                                <span className={`font-semibold ${isExpired ? 'text-red-600 bg-red-50 px-2 py-1 rounded border border-red-100' : 'text-gray-600'}`}>
                                                    {expDate.toLocaleDateString()}
                                                </span>
                                            );
                                        })()}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => handleRenew(user.id)}
                                                className="px-3 py-1.5 mr-2 flex items-center gap-1.5 text-sm font-semibold text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg transition-colors shadow-sm"
                                                title="Renovar al próximo ciclo"
                                            >
                                                <RefreshCw className="w-4 h-4" />
                                                Renovar
                                            </button>
                                            <Link
                                                to={`/admin/platformUsers/edit/${user.id}`}
                                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                title="Editar"
                                            >
                                                <Edit className="w-5 h-5" />
                                            </Link>
                                            <button
                                                onClick={() => handleDelete(user.id)}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                title="Eliminar"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {filteredUsers.length === 0 && (
                    <div className="p-8 text-center text-gray-500">
                        No se encontraron usuarios
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminUsers;
